import React from "react";
import { Box, Divider, IconButton } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { Link, useSearchParams } from "react-router-dom";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTable from "components/Shared/AppTable";
import { useLazyNoVendorDocsQuery } from "store/services/reportApi";
import { useTablePagination } from "hooks";
import HighlightedText from "components/Shared/HighlightedText";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import ExportNoVendorDocsExcel from "./ExportNoVendorDocsExcel";

const NoVendorDocsReport = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [
    {
      title: "Reports",
      path: "/app/crm/reports/list",
    },
    {
      title: "No Vendor Docs Report",
    },
  ];

  const columns = [
    {
      name: "Shop Name",
      selector: (row) => row?.shopName,
      cell: (row) => (
        <div title={row?.shopName} className="line-clamp-2">
          {row?.shopName ? (
            <HighlightedText text={row?.shopName} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "Owner Name",
      selector: (row) => row?.ownerName,
      cell: (row) => (
        <div title={row?.ownerName} className="line-clamp-2">
          {row?.ownerName ? (
            <HighlightedText text={row?.ownerName} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "Phone",
      selector: (row) => row?.phone,
      cell: (row) => (
        <div title={row?.phone} className="line-clamp-2">
          {row?.phone ? (
            <HighlightedText text={row?.phone} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "Address",
      selector: (row) => row?.address,
      cell: (row) => (
        <div title={row?.address} className="line-clamp-2">
          {row?.address ? (
            <HighlightedText text={row?.address} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </div>
      ),
      minWidth: "200px",
    },
    {
      name: "Onboarded By",
      selector: (row) => row?.onboardedBy,
      cell: (row) => row?.onboardedBy || "N/A",
      minWidth: "150px",
    },
    {
      name: "Added By",
      selector: (row) => row?.addedUser,
      cell: (row) => row?.addedUser || "N/A",
      minWidth: "150px",
    },
    {
      name: "Relationship Manager",
      selector: (row) => row?.relationshipManager,
      cell: (row) => row?.relationshipManager || "N/A",
      minWidth: "150px",
    },
    {
      name: "Referred By",
      selector: (row) => row?.referredBy,
      cell: (row) => row?.referredBy || "N/A",
      minWidth: "150px",
    },
    {
      name: "Created At",
      selector: (row) =>
        <AppTableDateTime date={row?.createdAt} dateOnly /> ?? "N/A",
    },
    {
      name: "Actions",
      cell: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
          <Link to={`/app/crm/vendor-profile/${row?._id}/detail`}>
            <IconButton
              size="small"
              aria-label="View Vendor Profile"
              color="primary"
            >
              <InfoOutlined />
            </IconButton>
          </Link>
        </Box>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [
    fetchNoVendorDocs,
    { data: shipmentRes, isLoading, isFetching, error },
  ] = useLazyNoVendorDocsQuery();

  React.useEffect(() => {
    fetchNoVendorDocs({
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [fetchNoVendorDocs, currentPage, perPage, keyword]);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={1}
        >
          <TableFilter
            searchLabel="Search by Name/Shop/Address"
            disabled={isLoading || isFetching}
          />
          <ExportNoVendorDocsExcel />
        </Box>
        <Divider />
        <AppTable
          columns={columns}
          data={shipmentRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={shipmentRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default NoVendorDocsReport;